import { Toaster } from '@fluentui/react-components';
import { QueryClientProvider, QueryErrorResetBoundary } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { TOASTER_ID } from '@/hooks/useToaster';
import { ThemeProvider } from '@/theme/ThemeProvider';

import { createQueryClient } from './queryClient';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Composition root. The nesting order is load-bearing:
 *
 *  ThemeProvider (owns the single FluentProvider)
 *   └─ QueryClientProvider          — AuthProvider needs a query client
 *       └─ QueryErrorResetBoundary  — supplies `reset` to the boundary below
 *           └─ ErrorBoundary        — so "Try again" refetches, not just re-renders
 *               └─ AuthProvider     — needs to be inside the boundary
 *
 * The Toaster sits inside FluentProvider so toasts pick up the current theme.
 */
export function AppProviders({ children }: AppProvidersProps) {
  // useState, not a module-level singleton: one client per mount keeps tests
  // isolated and survives Fast Refresh without stale caches.
  const [queryClient] = useState(createQueryClient);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary onReset={reset}>
              <AuthProvider>
                {children}
                <Toaster
                  toasterId={TOASTER_ID}
                  position="bottom-end"
                  pauseOnHover
                  pauseOnWindowBlur
                />
              </AuthProvider>
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
